import React from 'react';

import { Box, Collapse } from '@mui/material';

import Checkbox from '@mui/material/Checkbox';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { observer } from 'mobx-react';

import { useStore } from 'utils/hooks/store';

import { RelevantFieldType } from 'utils/api/modules/entities/entities.types';

import { Container, Header, SectionTitle } from 'containers/PipelinesDetails/components/styles';

import { buildEventFieldCatalogue, valueLabel } from './conditions/eventFields';
import { describeRule, hasRule } from './conditions/model';
import RelevantFieldRow from './RelevantFieldRow';

const RelevantFieldsSection = ({ title, fields }: { title: string; fields: string[] }): React.ReactElement => {
  const { entityFields, pipelineDetails, session } = useStore();

  const [expanded, setExpanded] = React.useState(true);

  const conditionsEnabled = Boolean(session.user?.conditionalStageFieldsEnabled);

  const stage = pipelineDetails.data?.stages.find((s) => s.key === pipelineDetails.selectedStage);
  const relevantFields = stage?.relevantFields ?? [];

  const inPipelineKeys = new Set(
    (pipelineDetails.data?.stages ?? []).flatMap((s) => (s.relevantFields ?? []).map((f) => f.key))
  );
  const catalogue = buildEventFieldCatalogue(entityFields.data, inPipelineKeys);

  if (!pipelineDetails.selectedStage || !stage?.relevantFields) {
    return <></>;
  }

  const commit = (next: RelevantFieldType[]): void => {
    pipelineDetails.updateStageData(pipelineDetails.selectedStage!, { relevantFields: next });
  };

  const toggleField = (fieldKey: string): void => {
    commit(
      relevantFields.some((field) => field.key === fieldKey)
        ? relevantFields.filter((field) => field.key !== fieldKey)
        : [...relevantFields, { key: fieldKey, required: false }]
    );
  };

  const patchField = (fieldKey: string, patch: Partial<RelevantFieldType>): void => {
    commit(
      relevantFields.map((field) => {
        if (field.key !== fieldKey) {
          return field;
        }
        const next = { ...field, ...patch };
        if (next.conditions === undefined) {
          delete next.conditions;
        }
        return next;
      })
    );
  };

  const checkWholeSection = fields.every((field) => relevantFields.some(({ key }) => key === field));

  const handleCheckboxForWholeSection = (event: React.ChangeEvent<HTMLInputElement>): void => {
    commit(
      event.target.checked
        ? [
            ...relevantFields,
            ...fields
              .filter((f) => !relevantFields.some(({ key }) => key === f))
              .map((f) => ({ key: f, required: false })),
          ]
        : relevantFields.filter(({ key }) => !fields.includes(key))
    );
  };

  return (
    <Container>
      <Header
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((previous) => !previous);
        }}
        $expanded={expanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={checkWholeSection}
            onChange={handleCheckboxForWholeSection}
            onClick={(e) => e.stopPropagation()}
            sx={{ padding: '4px', paddingRight: '8px' }}
          />
          <SectionTitle>{title}</SectionTitle>
        </Box>
        {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </Header>
      <Collapse in={expanded}>
        <Box sx={{ padding: '12px', gap: '8px', display: 'flex', flexDirection: 'column' }}>
          {fields.map((field) => {
            const fieldData = entityFields.data?.find((fieldItem) => fieldItem.id === field);

            if (!fieldData) return null;

            const relevantField = relevantFields.find(({ key }) => key === fieldData.id);

            return (
              <RelevantFieldRow
                key={`${pipelineDetails.selectedStage}-${fieldData.id}`}
                fieldKey={fieldData.id}
                name={fieldData.name}
                checked={!!relevantField}
                required={relevantField?.required ?? false}
                conditions={relevantField?.conditions}
                catalogue={catalogue}
                conditionsEnabled={conditionsEnabled}
                readBack={
                  relevantField && hasRule(relevantField)
                    ? describeRule(
                        relevantField.conditions,
                        (key) => catalogue.labels[key] ?? key,
                        (attribute, value) => valueLabel(catalogue, attribute, value)
                      )
                    : ''
                }
                onToggle={() => toggleField(fieldData.id)}
                onRequiredChange={(required) => patchField(fieldData.id, { required })}
                onConditionsChange={(conditions) => patchField(fieldData.id, { conditions })}
              />
            );
          })}
        </Box>
      </Collapse>
    </Container>
  );
};

export default observer(RelevantFieldsSection);
